import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "statistics-for-economics-probability-distributions-notes",
  category: "subject-explainers",
  authorSlug: "uday-kumar",
  title: "Statistics for Economics: Probability Distributions, Hypothesis Testing & Regression Notes",
  description: "Master statistics for economics with our comprehensive notes on descriptive stats, probability distributions, hypothesis testing, and regression analysis.",
  pubDate: "2026-08-20",
  tags: ["statistics", "probability", "hypothesis-testing", "regression"],
  readMinutes: 13,
  heroEyebrow: "Subject Explainer · Statistics",
};

export function Body() {
  return (
    <article className="max-w-4xl mx-auto space-y-8">
      <header className="mb-10">
        <h1 className="text-4xl font-bold mb-4 text-primary">Statistics for Economics</h1>
        <p className="text-xl text-muted-foreground">Probability Distributions, Hypothesis Testing &amp; Regression Notes</p>
      </header>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">1. Descriptive Statistics</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Central Tendency:</strong> Mean (average), Median (middle value), Mode (most frequent).</li>
          <li><strong>Dispersion:</strong> Variance (average squared deviation from mean), Standard Deviation (square root of variance).</li>
          <li><strong>Shape:</strong>
            <ul className="list-circle pl-6">
              <li>Skewness: Measures asymmetry. Positive skew (tail to the right, Mean &gt; Median). Negative skew (tail to the left, Mean &lt; Median).</li>
              <li>Kurtosis: Measures "tailedness". Leptokurtic (fat tails), Platykurtic (thin tails), Mesokurtic (normal).</li>
            </ul>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">2. Probability Theory</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Conditional Probability:</strong> <span className="font-mono text-grade">P(A|B) = P(A and B) / P(B)</span>. Probability of event A given B has occurred.</li>
          <li><strong>Bayes' Theorem:</strong> <span className="font-mono text-grade">P(A|B) = [P(B|A) * P(A)] / P(B)</span>. Crucial for updating beliefs based on new evidence.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">3. Probability Distributions</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-2">Discrete Distributions</h3>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Binomial:</strong> Number of successes in n independent Bernoulli trials. <span className="font-mono text-grade">P(X=k) = nCk * p^k * (1-p)^(n-k)</span>. Mean = np, Var = np(1-p).</li>
          <li><strong>Poisson:</strong> Number of events occurring in a fixed interval of time/space. <span className="font-mono text-grade">P(X=k) = (lambda^k * e^-lambda) / k!</span>. Mean = lambda, Var = lambda.</li>
          <li><strong>Geometric:</strong> Number of trials needed to get the first success.</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-2">Continuous Distributions</h3>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Normal Distribution:</strong> Bell-shaped curve. Defined by mean (mu) and variance (sigma^2). Standard Normal Z ~ N(0,1).</li>
          <li><strong>Chi-Square Distribution:</strong> Sum of squared independent standard normal variables. Used for testing variance and goodness of fit.</li>
          <li><strong>Student's t-distribution:</strong> Used when sample size is small and population variance is unknown. Has fatter tails than Normal.</li>
          <li><strong>F-distribution:</strong> Ratio of two independent Chi-Square variables divided by their degrees of freedom. Used for ANOVA and testing equality of variances.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">4. Hypothesis Testing</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Null (H0) vs Alternative (H1):</strong> H0 represents the status quo (e.g., mean = 0). H1 is what we want to prove.</li>
          <li><strong>Errors:</strong>
            <ul className="list-circle pl-6">
              <li>Type I Error (Alpha): Rejecting H0 when it is actually true (False Positive).</li>
              <li>Type II Error (Beta): Failing to reject H0 when H1 is true (False Negative).</li>
            </ul>
          </li>
          <li><strong>p-value:</strong> The probability of observing a test statistic as extreme as, or more extreme than, the one computed from sample data, assuming H0 is true. Reject H0 if p-value &lt; Alpha.</li>
          <li><strong>Confidence Intervals:</strong> A range of values that is likely to contain the true population parameter with a specified level of confidence (e.g., 95%).</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">5. Linear Regression</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Simple Linear Regression:</strong> <span className="font-mono text-grade">Y = beta_0 + beta_1 * X + epsilon</span>. Estimates relationship between one independent and one dependent variable.</li>
          <li><strong>Ordinary Least Squares (OLS) Assumptions (Gauss-Markov):</strong>
            <ol className="list-decimal pl-6">
              <li>Linearity in parameters.</li>
              <li>Random sampling.</li>
              <li>No perfect collinearity (for multiple regression).</li>
              <li>Zero conditional mean of errors: E(epsilon | X) = 0.</li>
              <li>Homoskedasticity: Var(epsilon | X) = sigma^2 (constant).</li>
            </ol>
            *If these hold, OLS is BLUE (Best Linear Unbiased Estimator).*
          </li>
          <li><strong>R-squared (Coefficient of Determination):</strong> Proportion of variance in the dependent variable predictable from the independent variable(s). <span className="font-mono text-grade">R^2 = ESS / TSS</span>.</li>
        </ul>
      </section>
    </article>
  );
}


